import React, { useEffect, useState } from "react";
import { Check, Edit3, FileText, X } from "lucide-react";

function NotesSection({ note = "", loading = false, onNoteSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(note || "");

  useEffect(() => {
    setValue(note || "");
  }, [note]);

  const handleSave = async () => {
    if (loading) {
      return;
    }
    const trimmedValue = value.trim();
    try {
      await onNoteSave?.(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save note.", error);
    }
  };

  const handleCancel = () => {
    setValue(note || "");
    setIsEditing(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      handleSave();
    }
    if (event.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <div className="min-w-0">
      {!isEditing && (
        <>
          {note?.trim() ? (
            <div className="group relative overflow-hidden rounded-md border border-border-light bg-card-bg px-3 py-2.5 shadow-[0_1px_4px_rgba(37,99,235,0.025)] transition-all duration-200 hover:border-theme-100 hover:shadow-[0_3px_10px_rgba(61,155,210,0.06)] dark:border-dark-border dark:bg-dark-card">
              <div className="absolute left-0 top-3 bottom-3 w-0.5 rounded-full bg-primary/60 dark:bg-dark-primary" />

              <div className="flex min-w-0 items-start gap-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-theme-100 bg-theme-50 text-primary dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary">
                  <FileText size={12} strokeWidth={1.8} />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="mb-1 text-[8px] font-semibold uppercase tracking-[0.07em] text-text-muted dark:text-dark-text-muted">
                    Note
                  </p>

                  <p className="line-clamp-5 text-[9px] leading-[1.6] text-text-secondary dark:text-dark-text-secondary" title={note}>
                    {note}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border-light bg-app-bg px-3 py-4 dark:border-dark-border dark:bg-dark-soft-blue">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-theme-100 bg-theme-50 text-primary dark:border-dark-border dark:bg-dark-card dark:text-dark-primary">
                <FileText size={13} strokeWidth={1.8} />
              </div>

              <p className="mt-2 text-[9px] font-medium text-text-secondary dark:text-dark-text-secondary">
                No note added
              </p>

              <p className="mt-0.5 text-center text-[8px] text-text-muted dark:text-dark-text-muted">
                Add a note to keep important information
              </p>
            </div>
          )}

          <button type="button" disabled={loading} onClick={() => setIsEditing(true)} className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-theme-100 bg-theme-50/50 px-2 py-2 text-[9px] font-semibold text-primary transition-all duration-200 hover:border-theme-200 hover:bg-theme-50 hover:shadow-[0_2px_7px_rgba(61,155,210,0.06)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary dark:hover:border-dark-primary/30">
            <Edit3 size={11} strokeWidth={1.9} />
            <span>{note?.trim() ? "Edit Note" : "Add Note"}</span>
          </button>
        </>
      )}

      {isEditing && (
        <div className="rounded-xl border border-theme-100 bg-card-bg p-1.5 shadow-[0_3px_12px_rgba(61,155,210,0.07)] dark:border-dark-border dark:bg-dark-card">
          <div className="rounded-lg border border-border-light bg-app-bg transition-colors focus-within:border-theme-200 focus-within:bg-card-bg dark:border-dark-border dark:bg-dark-soft-blue dark:focus-within:border-dark-primary/30">
            <textarea value={value} onChange={(event) => setValue(event.target.value)} onKeyDown={handleKeyDown} placeholder="Write a note..." autoFocus disabled={loading} rows={5} className="block w-full resize-none bg-transparent px-2.5 py-2.5 text-[9px] leading-[1.6] text-text-secondary outline-none placeholder:text-text-muted dark:text-dark-text-primary dark:placeholder:text-dark-text-muted" />
          </div>

          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="min-w-0 truncate text-[7px] text-text-muted dark:text-dark-text-muted">
              Ctrl + Enter to save
            </span>

            <div className="flex shrink-0 items-center gap-1.5">
              <button type="button" disabled={loading} onClick={handleCancel} title="Cancel" className="flex h-7 w-7 items-center justify-center rounded-lg border border-border-light bg-card-bg text-text-muted transition-all duration-200 hover:border-red-100 hover:bg-red-50 hover:text-red-500 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 dark:border-dark-border dark:bg-dark-soft-blue">
                <X size={11} strokeWidth={2} />
              </button>

              <button type="button" disabled={loading} onClick={handleSave} title="Save note" className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-white shadow-[0_2px_6px_rgba(61,155,210,0.14)] transition-all duration-200 hover:bg-primary-dark active:scale-95 disabled:cursor-not-allowed disabled:opacity-40">
                <Check size={11} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotesSection;